import { getRandomColor } from '@components/ArtBoard/ColorLibrary/utils/getRandomColor'
import {
  thunkAddColor,
  thunkDeleteColor,
} from '@store/migrationActions/thunkActions/thunkColorsActions'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import { notificationActionCreators } from '@storeOld/reducers/notification'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { ColorLibrary } from '../../../ColorLibrary'
import { ColorLegend } from './ColorLegend'
import css from './Colors.module.scss'

export const Colors: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()
  const colors = useSelector(migrationProjectSelector.getProjectColors)
  // the artboard is off: the palette and the legend are shown, not edited
  const artboardDisabled = useArtBoardDisabled()

  const {
    updateColorHexCode,
    updateColorOrder,
    changeColorTitle,
    changeIsShowInLegend,
  } = useProjectLegacyActions()

  const handleMoveColor = (id: string, index: number) => {
    updateColorOrder({ id, order: index })
  }

  const handleChangeColor = (id: string, hex: string) => {
    const availableColor = colors.find(
      (item) => item.hexCode === hex && item.id !== id
    )

    if (availableColor) {
      const duplicateColorName = availableColor?.title
      const croppedName = `${duplicateColorName?.slice(0, 32)}...`

      dispatch(
        notificationActionCreators.setCustomNotification({
          type: 'coral',
          toastContainerId: 'secondary',
          text: `This color already using as ${croppedName}. Please pick another color`,
        })
      )
    } else {
      updateColorHexCode({ id, hexCode: hex })
      sendGtmEvent(GtmTriggerName.COLOR_EDIT, 'Change color result')
    }
  }

  const handleAddNewColor = () => {
    const randomHexCode = getRandomColor()
    dispatch(thunkAddColor({ hexCode: randomHexCode }))
    sendGtmEvent(GtmTriggerName.COLOR_ADD, 'New color is added to the palette')
  }

  const handleRemoveColor = (id: string) => {
    dispatch(thunkDeleteColor(id))
  }

  const handleRenameColor = (id: string, title: string) => {
    changeColorTitle({ id, title })
  }

  const handleSwitchColor = (id: string, showInLegend: boolean) => {
    changeIsShowInLegend({ id, showInLegend })
  }

  return (
    <div className={css.wrapper}>
      <ColorLegend disabled={artboardDisabled} />
      <ColorLibrary
        colors={colors}
        disabled={artboardDisabled}
        onChangeColor={handleChangeColor}
        onAddNewColor={handleAddNewColor}
        onMoveColor={handleMoveColor}
        onRemoveColor={handleRemoveColor}
        onRenameColor={handleRenameColor}
        onSwitchColor={handleSwitchColor}
      />
    </div>
  )
}
